import React, {useState} from 'react';
import {
  List,
  BulkDeleteWithConfirmButton,
  Datagrid,
  TextField,
  Filter,
  TextInput,
  ImageField,
  DateInput
} from 'react-admin';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip
} from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';

const ToolsFilter = (props) => (
  <Filter {...props}>
    <DateInput source="startDate" alwaysOn />
    <DateInput source="endDate" alwaysOn />
    <TextInput source="vendorId" label="Vendor Id" />
    <TextInput source="courseName" label="Course Name" alwaysOn />
    <TextInput source="description" label="Description" />
  </Filter>
);

const ApprovalStatusField = ({record}) => {
  if (!record) return null;
  const isApproved = record.isAdminApproved === true || record.isAdminApproved === 'true';
  return (
    <Chip
      label={isApproved ? 'Approved' : 'Pending'}
      style={{
        backgroundColor: isApproved ? '#4caf50' : '#ff9800',
        color: '#fff',
        fontWeight: 'bold'
      }}
      size="small"
    />
  );
};

const SpecificationField = ({record}) => {
  const [open, setOpen] = useState(false);

  if (!record) return null;

  const handleOpen = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setOpen(true);
  };

  const handleClose = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setOpen(false);
  };

  const specs = record.trainingSpecificationDetails || record.specifications || [];

  return (
    <>
      <Tooltip title="View Specifications">
        <IconButton color="primary" onClick={handleOpen} size="small">
          <AssignmentIcon />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth onClick={(e) => e.stopPropagation()}>
        <DialogTitle style={{backgroundColor: '#1A3254', color: '#fff'}}>
          Course Specifications - {record.courseName}
        </DialogTitle>
        <DialogContent style={{padding: '20px'}}>
          {specs && specs.length > 0 ? (
            <Table size="small">
              <TableHead style={{backgroundColor: '#f0f0f0'}}>
                <TableRow>
                  <TableCell>
                    <b>Specification Name</b>
                  </TableCell>
                  <TableCell>
                    <b>Type</b>
                  </TableCell>
                  <TableCell>
                    <b>Duration</b>
                  </TableCell>
                  <TableCell>
                    <b>Cost (₹)</b>
                  </TableCell>
                  <TableCell>
                    <b>Description</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {specs.map((spec, index) => (
                  <TableRow key={spec.id || index}>
                    <TableCell>{spec.specificationName || '-'}</TableCell>
                    <TableCell>{spec.type || '-'}</TableCell>
                    <TableCell>{spec.duration ? `${spec.duration} ${spec.durationPeriod || ''}` : '-'}</TableCell>
                    <TableCell>{spec.cost || '-'}</TableCell>
                    <TableCell>{spec.description || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body1" style={{padding: '20px', textAlign: 'center'}}>
              No training specification details found for this course.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CourseSpecificationExpand = ({record}) => {
  if (!record) return null;
  const specs = record.trainingSpecificationDetails || record.specifications || [];
  return (
    <div style={{padding: '16px', backgroundColor: '#fafafa'}}>
      <Typography variant="h6" style={{marginBottom: '8px', color: '#1A3254'}}>
        Training Specifications
      </Typography>
      {specs && specs.length > 0 ? (
        <Table size="small">
          <TableHead style={{backgroundColor: '#eeeeee'}}>
            <TableRow>
              <TableCell>
                <b>Specification Name</b>
              </TableCell>
              <TableCell>
                <b>Type</b>
              </TableCell>
              <TableCell>
                <b>Duration</b>
              </TableCell>
              <TableCell>
                <b>Cost (₹)</b>
              </TableCell>
              <TableCell>
                <b>Description</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {specs.map((spec, index) => (
              <TableRow key={spec.id || index}>
                <TableCell>{spec.specificationName || '-'}</TableCell>
                <TableCell>{spec.type || '-'}</TableCell>
                <TableCell>{spec.duration ? `${spec.duration} ${spec.durationPeriod || ''}` : '-'}</TableCell>
                <TableCell>{spec.cost || '-'}</TableCell>
                <TableCell>{spec.description || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="textSecondary">
          No training specification details found.
        </Typography>
      )}
    </div>
  );
};

const rowClick = (id, basePath, record) => {
  if (record.id === 0) {
    return 'show';
  }
  return 'edit';
};

const ToolsBulkActionButtons = (props) => <BulkDeleteWithConfirmButton {...props} />;

const DescriptionField = ({record}) => {
  if (!record || !record.description) return null;
  return (
    <div
      style={{
        maxWidth: 250,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-word'
      }}
      title={record.description}
    >
      {record.description}
    </div>
  );
};

const FormattedDateField = ({record, source}) => {
  if (!record || !record[source]) return null;
  const date = new Date(record[source]);
  if (isNaN(date.getTime())) return <span>{record[source]}</span>;

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = date.getHours() >= 12 ? 'pm' : 'am';

  return <span>{`${day}-${month}-${year} | ${hours}:${minutes}${ampm}`}</span>;
};

export const ToolsList = ({classes, ...props}) => (
  <List title=" Courses" {...props} filters={<ToolsFilter />} bulkActionButtons={<ToolsBulkActionButtons />}>
    <Datagrid rowClick={rowClick} expand={<CourseSpecificationExpand />}>
      <TextField source="vendorId" label="Vendor Id" />
      <TextField source="courseName" label="Course Name" />
      <DescriptionField label="Description" />
      <ApprovalStatusField label="Admin Approval Status" />
      <SpecificationField label="Specifications" />
      <ImageField source="courseImagePath" label="Photo" classes={classes} />
      <FormattedDateField source="createdAt" label="Created At" />
    </Datagrid>
  </List>
);

export default ToolsList;
